// "Save this area for offline": pre-fetch terrain and base-map tiles around a point so the
// service worker caches them before the pilot loses signal on launch.

import { lngLatToTile, offset, type LngLat } from './geo';
import { zoomForResolution } from '../sources/terrarium';
import { FONT, FONT_BOLD, FONT_ITALIC } from '../map/basestyle';

export function tileUrls(template: string | string[], centre: LngLat, radius: number, zooms: number[]): string[] {
  const t = Array.isArray(template) ? template : [template];
  const nw = offset(offset(centre, radius, 0), radius, 270);
  const se = offset(offset(centre, radius, 180), radius, 90);
  const out: string[] = [];
  for (const z of zooms) {
    const a = lngLatToTile(nw, z);
    const b = lngLatToTile(se, z);
    for (let y = Math.floor(a.y); y <= Math.floor(b.y); y++)
      for (let x = Math.floor(a.x); x <= Math.floor(b.x); x++) {
        const tpl = t[(x + y) % t.length];
        out.push(tpl.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y)));
      }
  }
  return out;
}

export function offlineTiles(centre: LngLat, radius: number, cell: number, terrainUrl: string, basemap: string[]): string[] {
  const zt = zoomForResolution(centre[1], cell, 13);
  const zr = [10, 11, 12]; // relief rendering (DEM is capped at z12)
  const zp = zoomForResolution(centre[1], 60, 13); // profile sampling
  const fonts = [FONT, FONT_BOLD, FONT_ITALIC].flatMap((f) =>
    ['0-255', '256-511', '8192-8447', '9472-9727'].map((r) => `/fonts/${encodeURIComponent(f[0])}/${r}.pbf`),
  );
  return [
    ...fonts,
    ...tileUrls(terrainUrl, centre, radius, [...new Set([zt, zp, ...zr])]),
    // Relief (hillshade, tint, contours) is computed from the terrain tiles above. Raster base
    // maps need their own tiles; vector base-map tiles are not pre-fetched yet.
    ...(basemap.length ? tileUrls(basemap, centre, radius, [9, 10, 11, 12, 13]) : []),
  ];
}

export async function prefetch(urls: string[], onProgress: (done: number, total: number) => void): Promise<number> {
  let done = 0;
  let failed = 0;
  const queue = [...urls];
  const worker = async () => {
    while (queue.length) {
      const u = queue.shift()!;
      try {
        const r = await fetch(u, { mode: 'cors' });
        if (!r.ok) failed++;
      } catch {
        failed++;
      }
      onProgress(++done, urls.length);
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  return failed;
}
